
import { ReactNode, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useLocation } from "react-router-dom";
import { FolderHeart, Lock, Settings, Star } from "lucide-react";

interface ProfileTab {
  id: string;
  label: string;
  content?: ReactNode;
  condition?: boolean;
  onClick?: () => void;
  navigate?: string;
  icon?: ReactNode;
}

interface ProfileTabsProps {
  tabs: ProfileTab[];
  defaultTab: string;
}

export const ProfileTabs = ({ tabs, defaultTab }: ProfileTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const filteredTabs = tabs.filter(tab => tab.condition !== false);
  
  // Sync URL hash with active tab on mount and location change
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && filteredTabs.some(tab => tab.id === hash)) {
      setActiveTab(hash);
    } else if (!hash && defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [location, defaultTab, filteredTabs]);
  
  const handleTabChange = (value: string) => {
    console.log(`Changing tab to: ${value}`);
    setActiveTab(value);
    
    const selectedTab = filteredTabs.find(tab => tab.id === value);
    
    if (selectedTab?.onClick) {
      selectedTab.onClick();
    }
    
    if (selectedTab?.navigate) {
      console.log(`Navigating to: ${selectedTab.navigate}`);
      navigate(selectedTab.navigate);
      return;
    }
    
    // Update URL hash if we're on the profile page
    if (location.pathname === '/profile') {
      if (value === 'profile') {
        // Remove hash for default tab
        navigate('/profile', { replace: true });
      } else {
        // Add hash for other tabs
        navigate(`/profile#${value}`, { replace: true });
      }
    }
  };
  
  return (
    <Tabs 
      value={activeTab} 
      className="w-full animate-fade-in"
      onValueChange={handleTabChange}
    >
      <TabsList className="mb-6 grid grid-cols-3 md:w-fit md:grid-cols-none md:flex">
        {filteredTabs.map((tab) => (
          <TabsTrigger 
            key={tab.id} 
            value={tab.id}
            className="transition-all duration-200 data-[state=active]:shadow-sm"
          >
            {tab.icon && <span className="mr-2">{tab.icon}</span>}
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {filteredTabs.map((tab) => (
        tab.content && (
          <TabsContent 
            key={tab.id} 
            value={tab.id}
            className="animate-fade-in"
          >
            {tab.content}
          </TabsContent>
        )
      ))}
    </Tabs>
  );
};
